import { ScreenVoteDecision } from 'pages/StudyList/StudyList';
import { getCsrfToken } from 'util/authenticityToken';
import { VOTES_ROUTE, tokenizeRoute } from 'query/routes';

export type VoteErrorReason =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'trialLimitReached'
  | 'notFound'
  | 'conflict'
  | 'invalid'
  | 'server'
  | 'unknown';

export type VoteFailure = {
  reason: VoteErrorReason;
  // 0 when there was no response
  status: number;
  serverMessage?: string;
};

export type SavedVote = {
  id: number;
  vote: ScreenVoteDecision;
};

export type VoteResult =
  | { ok: true; vote: SavedVote; isTrialLimitReached: boolean }
  | { ok: false; error: VoteFailure; isTrialLimitReached: boolean };

type VoteResponseBody = {
  vote?: SavedVote;
  errors?: string[];
};

// Some failures have no JSON body
const readJson = async (
  response: Response
): Promise<VoteResponseBody | undefined> => {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
};

const REASON_BY_STATUS: Partial<Record<number, VoteErrorReason>> = {
  401: 'unauthorized',
  402: 'trialLimitReached',
  403: 'forbidden',
  404: 'notFound',
  409: 'conflict',
  422: 'invalid',
  500: 'server',
};

const toFailure = (status: number, body?: VoteResponseBody): VoteResult => {
  const reason = REASON_BY_STATUS[status] ?? 'unknown';

  return {
    ok: false,
    error: { reason, status, serverMessage: body?.errors?.[0] },
    isTrialLimitReached: reason === 'trialLimitReached',
  };
};

export const createScreeningVote = async ({
  reviewId,
  studyId,
  vote,
  consensus = false,
}: {
  reviewId: string;
  studyId: number;
  vote: ScreenVoteDecision;
  consensus?: boolean;
}): Promise<VoteResult> => {
  const url = tokenizeRoute(VOTES_ROUTE, {
    review_id: reviewId,
    study_id: studyId,
  });

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
      },
      body: JSON.stringify({ vote, consensus, study_category: 'Screen' }),
    });
  } catch {
    return {
      ok: false,
      error: { reason: 'network', status: 0 },
      isTrialLimitReached: false,
    };
  }

  const body = await readJson(response);

  if (response.ok && body?.vote) {
    return { ok: true, vote: body.vote, isTrialLimitReached: false };
  }

  // When the vote saves but uses up the last vote of the trial, the server still returns 402
  if (response.status === 402 && body?.vote) {
    return { ok: true, vote: body.vote, isTrialLimitReached: true };
  }

  return toFailure(response.status, body);
};
